import { Injectable } from '@angular/core';

import { IMarker as _IMarker } from '@lib/geo/map';
import { DistanceUnit } from '@lib/map-geometry';

import { GoogleMap } from '../../../map';
import { IGoogleMarker } from '../interfaces/google-marker.interface';
import { GoogleMarkerGeometryService } from './google-marker-geometry.service';

type IMarker = _IMarker<GoogleMap>;

/**
 * Service responsible for marker query and search operations.
 * Handles finding, filtering, and searching through marker collections.
 */
@Injectable()
export class GoogleMarkerQueryService {
  private readonly _googleMarkers = new Map<string, IGoogleMarker>();

  constructor(private readonly _geometryService: GoogleMarkerGeometryService) {}

  setMarkersCollection(markers: Map<string, IGoogleMarker>): void {
    this._googleMarkers.clear();
    markers.forEach((marker, id) => {
      this._googleMarkers.set(id, marker);
    });
  }

  findMarkerById(id: string): IMarker | null {
    const googleMarker = this._googleMarkers.get(id);
    if (!googleMarker) return null;

    return (googleMarker as IGoogleMarker & { markerData?: IMarker }).markerData || null;
  }

  getAllMarkers(): IMarker[] {
    return Array.from(this._googleMarkers.values())
      .map((googleMarker) => (googleMarker as IGoogleMarker & { markerData?: IMarker }).markerData)
      .filter((markerData): markerData is IMarker => markerData != null);
  }

  getMarkersByAttribute(attribute: string, value: unknown): IMarker[] {
    return this.getAllMarkers().filter(
      (marker) => marker.attributes && marker.attributes[attribute] === value,
    );
  }

  getMarkersByEntityType(entityType: string): IMarker[] {
    return this.getAllMarkers().filter((marker) => marker.metadata?.entityType === entityType);
  }

  getVisibleMarkers(): IMarker[] {
    return this.getAllMarkers().filter((marker) => marker.visible);
  }

  getMarkersInBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): IMarker[] {
    return this.getAllMarkers().filter((marker) => {
      const { lat, lon } = marker.position;
      return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
    });
  }

  getMarkersWithinDistance(center: { lat: number; lon: number }, distanceKm: number): IMarker[] {
    return this.getAllMarkers().filter((marker) => {
      const centerMarker = { position: center } as IMarker;
      const distance = this._geometryService.calculateDistance(
        centerMarker,
        marker,
        DistanceUnit.MILES,
      );
      return distance <= distanceKm;
    });
  }

  searchMarkers(query: string): IMarker[] {
    const lowerQuery = query.toLowerCase();

    return this.getAllMarkers().filter((marker) => {
      if (marker.title && marker.title.toLowerCase().includes(lowerQuery)) {
        return true;
      }

      if (marker.attributes) {
        for (const [key, value] of Object.entries(marker.attributes)) {
          if (String(value).toLowerCase().includes(lowerQuery)) {
            return true;
          }
        }
      }

      if (marker.metadata?.customData) {
        for (const [key, value] of Object.entries(marker.metadata.customData)) {
          if (String(value).toLowerCase().includes(lowerQuery)) {
            return true;
          }
        }
      }

      return false;
    });
  }
}
